// Manufacturing DTOs: named stations + an append-only production log tied to a case. A log
// records what physically happened at a station (mill/print/thermoform/…); it MAY advance the
// case's status, but that goes through the single case-status writer, never a duplicate UPDATE.
import { z } from 'zod';
import { CASE_STATUSES } from './cases.js';

export const PRODUCTION_OPERATIONS = ['mill', 'print', 'sinter', 'glaze', 'qc_check', 'rework', 'other'] as const;
export type ProductionOperation = (typeof PRODUCTION_OPERATIONS)[number];
export const PRODUCTION_OPERATION_LABELS: Record<ProductionOperation, string> = {
  mill: 'Mill',
  print: '3D print',
  sinter: 'Sinter',
  glaze: 'Glaze / finish',
  qc_check: 'QC check',
  rework: 'Rework',
  other: 'Other',
};

export const PRODUCTION_OUTCOMES = ['ok', 'scrap', 'rework'] as const;
export type ProductionOutcome = (typeof PRODUCTION_OUTCOMES)[number];
export const PRODUCTION_OUTCOME_LABELS: Record<ProductionOutcome, string> = {
  ok: 'OK',
  scrap: 'Scrap',
  rework: 'Rework',
};

export const STATION_KINDS = ['milling', 'printing', 'sintering', 'ceramics', 'qc', 'other'] as const;
export type StationKind = (typeof STATION_KINDS)[number];
export const STATION_KIND_LABELS: Record<StationKind, string> = {
  milling: 'Milling',
  printing: 'Printing',
  sintering: 'Sintering',
  ceramics: 'Ceramics',
  qc: 'QC',
  other: 'Other',
};

export interface Station {
  id: string;
  name: string;
  kind: StationKind;
  isActive: boolean;
  createdAt: string;
}

export interface ProductionLog {
  id: string;
  caseId: string;
  stationId: string | null;
  stationName: string | null;
  operation: ProductionOperation;
  material: string | null;
  machine: string | null;
  quantity: number | null;
  durationMin: number | null;
  outcome: ProductionOutcome;
  notes: string | null;
  actorUserId: string | null;
  statusAfter: string | null;
  at: string;
  createdAt: string;
}

const optText = (max: number) => z.string().trim().max(max).nullish();

export const createLogSchema = z.object({
  caseId: z.string().min(1),
  stationId: z.string().min(1).nullish(),
  stationName: optText(120),
  operation: z.enum(PRODUCTION_OPERATIONS),
  material: optText(120),
  machine: optText(120),
  quantity: z.coerce.number().int().min(0).max(100000).nullish(),
  durationMin: z.coerce.number().int().min(0).max(100000).nullish(),
  outcome: z.enum(PRODUCTION_OUTCOMES).default('ok'),
  notes: optText(2000),
  // When present the server ALSO advances the case status (must satisfy canTransition).
  advanceTo: z.enum(CASE_STATUSES).nullish(),
});
export type CreateLogRequest = z.infer<typeof createLogSchema>;

export const createStationSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(STATION_KINDS),
});
export type CreateStationRequest = z.infer<typeof createStationSchema>;

export const updateStationSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    kind: z.enum(STATION_KINDS).optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateStationRequest = z.infer<typeof updateStationSchema>;

export interface LogListQuery {
  caseId?: string;
  stationId?: string;
  limit: number;
  offset: number;
}
